import { useState, useRef } from "react";
import jobService from "../../services/job.service";
import { Button, Card, CardHeader, Icon } from "../../ds";

/* Job Description — structured entry, no AI.
 *
 * Four fields the school fills directly (Shohini: "not open text"): Overview,
 * Key Responsibilities, Qualifications, Requirements. A JD document can be
 * uploaded to PRE-FILL those fields (its text is read and split into the four
 * sections, which the school then edits) and is recorded as the source. There
 * is no "generate" step. Save persists; Edit re-opens a saved description.
 *
 * HIR-HR1 (29 Oct): above them, what the vacancy was posted with (department,
 * subject, grades, type, experience, posts, closing date); below them, the
 * Key Result Areas the JD & KRA writer drafted, editable like the rest.
 */

const linesToArray = (text) =>
  String(text || "").split("\n").map((l) => l.trim()).filter(Boolean);
const arrayToLines = (arr) => (Array.isArray(arr) ? arr : []).join("\n");

/* Key result areas (HIR-HR1): one per line as "Area: how it is measured". */
const krasToLines = (kras) =>
  (Array.isArray(kras) ? kras : []).map((k) => (k.measure ? `${k.area}: ${k.measure}` : k.area)).join("\n");
const linesToKras = (text) =>
  linesToArray(text).map((l) => {
    const i = l.indexOf(":");
    return i > 0 ? { area: l.slice(0, i).trim(), measure: l.slice(i + 1).trim() } : { area: l, measure: "" };
  });

/* What the vacancy was posted with (HIR-HR1), shown above its description. */
const closingLabel = (closesAt) => {
  if (!closesAt) return "";
  // The closing instant is the last moment of a day in India.
  return new Date(closesAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
};
const factsOf = (job) => {
  const c = job?.careers || {};
  const exp = c.minExperienceYears === 0 ? "Freshers welcome"
    : (Number.isInteger(c.minExperienceYears) ? `${c.minExperienceYears}+ years` : "");
  return [
    ["Department", job?.department],
    ["Subject", c.area],
    ["Grades", c.gradeBand],
    ["Employment type", c.employmentType],
    ["Experience", exp],
    ["Posts", c.openings > 1 ? String(c.openings) : ""],
    ["Applications close", closingLabel(c.closesAt)],
  ].filter(([, v]) => v);
};

const JobDescription = ({ jobData, jobId, onSaved }) => {
  const jd = jobData?.jobDescription || {};
  const id = jobId || jobData?._id;
  const facts = factsOf(jobData);

  const initial = {
    overview: jd.overview || "",
    responsibilities: arrayToLines(jd.generatedResponsibilities),
    qualifications: arrayToLines(jd.generatedQualifications),
    requirements: arrayToLines(jd.requirements),
    kras: krasToLines(jd.kras),
  };

  const hasContent = !!(initial.overview || initial.responsibilities || initial.qualifications || initial.requirements);

  const [fields, setFields] = useState(initial);
  const [sourceFile, setSourceFile] = useState(jd.sourceFile || null);
  const [editing, setEditing] = useState(!hasContent);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [note, setNote] = useState("");
  const fileRef = useRef(null);

  const setField = (k, v) => setFields((f) => ({ ...f, [k]: v }));

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setNote("");
    try {
      const res = await jobService.uploadJobDescriptionFile(id, file);
      const d = res?.data || {};
      const f = d.fields || {};
      // Pre-fill from the document; the school edits from here.
      setFields((prev) => ({
        overview: f.overview || "",
        responsibilities: arrayToLines(f.responsibilities),
        qualifications: arrayToLines(f.qualifications),
        requirements: arrayToLines(f.requirements),
        // A JD document has no KRAs section; keep the ones already here.
        kras: prev.kras,
      }));
      if (d.sourceFile) setSourceFile(d.sourceFile);
      setEditing(true);
      setNote(d.warning || "Read the document in — review the fields and Save.");
    } catch (err) {
      setNote(err.response?.data?.message || "Could not read that file.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    setNote("");
    try {
      await jobService.updateJobDescription(id, {
        overview: fields.overview.trim(),
        responsibilities: linesToArray(fields.responsibilities),
        qualifications: linesToArray(fields.qualifications),
        requirements: linesToArray(fields.requirements),
        kras: linesToKras(fields.kras),
        ...(sourceFile ? { sourceFile } : {}),
      });
      setEditing(false);
      setNote("Saved.");
      if (onSaved) onSaved();
    } catch (err) {
      setNote(err.response?.data?.message || "Could not save — try again.");
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setFields(initial);
    setEditing(false);
    setNote("");
  };

  // Fetch a fresh short-lived link and open the stored document.
  const openSource = async () => {
    try {
      const res = await jobService.getJobDescriptionFileUrl(id);
      const url = res?.data?.url;
      if (url) window.open(url, "_blank", "noopener");
      else setNote("Could not open the file.");
    } catch {
      setNote("Could not open the file.");
    }
  };

  const muted = { margin: 0, fontSize: 'var(--fs-body-sm)', color: 'var(--text-muted)' };
  const Field = ({ label, k, placeholder, list }) => (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h4 style={{ margin: 0, fontSize: 'var(--fs-body-lg)', fontWeight: 600, color: 'var(--text-1)' }}>{label}</h4>
        {list && editing && <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-muted)' }}>One per line</span>}
      </div>
      {editing ? (
        <textarea
          value={fields[k]}
          onChange={(e) => setField(k, e.target.value)}
          placeholder={placeholder}
          rows={list ? 6 : 4}
          aria-label={label}
          style={{ width: '100%', boxSizing: 'border-box', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-md)', padding: '10px 14px', font: 'inherit', fontSize: 'var(--fs-body-md)', color: 'var(--text-1)', background: 'var(--surface-1)', resize: 'vertical' }}
        />
      ) : list ? (
        linesToArray(fields[k]).length ? (
          <ul style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 4, color: 'var(--text-2)' }}>
            {linesToArray(fields[k]).map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        ) : <p style={muted}>Not added</p>
      ) : (
        fields[k] ? <p style={{ margin: 0, color: 'var(--text-2)', whiteSpace: 'pre-wrap' }}>{fields[k]}</p>
                  : <p style={muted}>Not added</p>
      )}
    </div>
  );

  return (
    <Card>
      <CardHeader
        title="Job description"
        action={!editing ? (
          <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
            Edit
          </Button>
        ) : null}
      />
      <div style={{ display: 'grid', gap: 24 }}>
        {/* Upload / attach — only while editing */}
        {editing && (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, padding: 16, borderRadius: 'var(--radius-md)', border: '1px dashed var(--border-strong)', background: 'var(--surface-2)' }}>
            <input
              ref={fileRef}
              type="file"
              accept=".doc,.docx,.pdf"
              onChange={onPickFile}
              style={{ display: 'none' }}
            />
            <Button
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              icon={<Icon name="file-arrow-up" />}
            >
              {uploading ? "Reading…" : "Upload a JD file to pre-fill"}
            </Button>
            <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-muted)' }}>.doc, .docx or .pdf — its text fills the fields below, which you can edit.</span>
          </div>
        )}

        {/* Which document this came from */}
        {sourceFile?.name && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)' }}>
            <Icon name="file-text" style={{ color: 'var(--text-muted)' }} />
            <span>Source: </span>
            {sourceFile.key
              ? <button onClick={openSource} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'var(--brand-primary-ink)', textDecoration: 'underline' }}>{sourceFile.name}</button>
              : <span style={{ color: 'var(--text-1)' }}>{sourceFile.name}</span>}
            {!sourceFile.key && <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-muted)' }}>(file not stored)</span>}
          </div>
        )}

        {/* What the vacancy was posted with (HIR-HR1). */}
        {facts.length > 0 && (
          <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px 24px', margin: 0, padding: 16, borderRadius: 'var(--radius-md)', background: 'var(--surface-2)' }}>
            {facts.map(([label, value]) => (
              <div key={label}>
                <dt style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-muted)' }}>{label}</dt>
                <dd style={{ margin: 0, fontSize: 'var(--fs-body-sm)', fontWeight: 500, color: 'var(--text-1)' }}>{value}</dd>
              </div>
            ))}
          </dl>
        )}

        <Field label="Overview" k="overview" placeholder="A short summary of the role…" />
        <Field label="Key Responsibilities" k="responsibilities" placeholder="One responsibility per line…" list />
        <Field label="Qualifications" k="qualifications" placeholder="One qualification per line…" list />
        <Field label="Requirements" k="requirements" placeholder="One requirement per line…" list />
        <Field label="Key Result Areas (KRAs)" k="kras" placeholder="One per line, e.g. Lesson delivery: lessons follow the syllabus and are taught on time" list />

        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {editing && (
            <>
              <Button onClick={save} disabled={saving} icon={<Icon name={saving ? 'spinner' : 'check-circle'} spin={saving} />}>
                {saving ? "Saving…" : "Save"}
              </Button>
              {hasContent && (
                <Button variant="secondary" onClick={cancel} disabled={saving}>
                  Cancel
                </Button>
              )}
            </>
          )}
          {note && <span style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--text-muted)' }}>{note}</span>}
        </div>
      </div>
    </Card>
  );
};

export default JobDescription;
